import type { RegistrationFormData } from '../context/registration-context';
import { RegistrationStep } from '../types/registration-state.types';

// Registration flow step determination

/**
 * Determines the correct registration step based on available user data
 * @param userData - User data from API or partial signup
 * @param isPartialUser - Whether this is a partial signup user
 * @returns The registration step the user should be on
 */
export const determineRegistrationStep = (userData: Partial<RegistrationFormData>, isPartialUser: boolean = false): RegistrationStep => {
  console.log('🔍 Determining registration step for:', { userData, isPartialUser });
  
  // Check if we have guardian info (required for all users)
  const hasGuardianInfo = userData.guardianFirstName && 
                         userData.guardianLastName && 
                         (userData.email || userData.mobile);
  
  if (!hasGuardianInfo) {
    console.log('📋 No guardian info found - starting at GET_STARTED');
    return RegistrationStep.GET_STARTED;
  }
  
  // Check if we have basic client info
  const hasBasicInfo = userData.firstName && 
                      userData.lastName && 
                      userData.dob && 
                      userData.zipCode;
  
  if (!hasBasicInfo) {
    console.log('📋 Guardian info found but no basic client info - starting at BASIC_INFO');
    return RegistrationStep.BASIC_INFO;
  }
  
  // Check if we have personal questions data
  const hasPersonalQuestions = userData.isInSchoolDaycare !== undefined &&
                              userData.hasHadABAServices !== undefined &&
                              userData.primarySupportAreas &&
                              userData.primarySupportAreas.length > 0 &&
                              userData.openToTelehealth !== undefined &&
                              userData.availabilitySchedule;
  
  if (!hasPersonalQuestions) {
    console.log('📋 Basic info found but no personal questions - starting at PERSONAL_QUESTIONS');
    return RegistrationStep.PERSONAL_QUESTIONS;
  }
  
  // Check if we have insurance data
  const hasInsurance = userData.insuranceFiles && 
                      userData.insuranceFiles.length > 0 &&
                      userData.insuranceFiles.some((insurance: any) => 
                        insurance.frontImg && insurance.backImg
                      );
  
  if (!hasInsurance) {
    console.log('📋 Personal questions found but no insurance - starting at INSURANCE');
    return RegistrationStep.INSURANCE;
  }
  
  // Check if we have diagnostic data
  const hasDiagnosticData = userData.hasAutismDiagnosis !== undefined ||
                           userData.cdeUrl ||
                           userData.lmnUrl ||
                           userData.diagnosticEvaluationFile ||
                           userData.letterOfMedicalNecessityFile;
  
  if (!hasDiagnosticData) {
    console.log('📋 Insurance found but no diagnostic data - starting at DIAGNOSTIC');
    return RegistrationStep.DIAGNOSTIC;
  }
  
  // If we have all data, user should be at final results
  console.log('📋 All data found - user should be at FINAL_RESULTS');
  return RegistrationStep.FINAL_RESULTS;
};

/**
 * Determines if a user is a complete user (has all required data)
 * @param userData - User data from API
 * @returns Whether the user is complete
 */
export const isCompleteUser = (userData: Partial<RegistrationFormData>): boolean => {
  const step = determineRegistrationStep(userData, false);
  return step === RegistrationStep.FINAL_RESULTS;
};

/**
 * Determines if a partial user has all required data for their current state
 * @param userData - Partial user data
 * @returns Whether the partial user has all required data for their current step
 */
export const isPartialUserComplete = (userData: Partial<RegistrationFormData>): boolean => {
  const step = determineRegistrationStep(userData, true);
  return step > RegistrationStep.GET_STARTED;
}; 